import { useGateValue } from "@statsig/react-bindings";
import { ErrorMessage } from "@/components/common/Error.tsx";
import { useEmbedded } from "@/hooks/useEmbedded.tsx";
import { type FocusType } from "@/components/messages/Omnibox/Omnibox.tsx";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible.tsx";
import { useUnarchiveThread } from "@/hooks/useThreads.tsx";
import { cn } from "@/lib/utils.ts";
import type { DebugDataByView } from "@/components/messages/hooks/useMessageList.tsx";
import type { GetThreadResp } from "@/types/thread.ts";
import { RelocatedBiscuitProvider } from "@/components/messages/relocatedBiscuitContext.tsx";
import type { AnchoredBiscuit } from "@/shared/anchoredBiscuits.ts";
import { selectFeedLayout } from "@/shared/feedLayout.ts";
import { stepsLabel } from "@/shared/stepsLabel.ts";
import { HeavyArtifactItem } from "@/components/artifacts/HeavyArtifactItem.tsx";
import { ChevronDown } from "lucide-react";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { breakWordsContainer } from "../layout/constants.ts";
import { ScreenInnerScrollWrapper, ScreenOuterScrollWrapper } from "../layout/contentLayout.tsx";
import { Loader } from "../loader/Loader.tsx";
import { EmptyChatPlaceholder } from "./EmptyChatPlaceholder.tsx";
import { buildRootItems, childClusterItemKey, type RootItem } from "@/shared/eventTree.ts";
import { RootItemRenderer } from "./EventItems.tsx";
import { useFeedScroll } from "./hooks/useFeedScroll.tsx";
import { type SendMessageFn, useMessageList } from "./hooks/useMessageList.tsx";
import { MessageItem } from "./MessageItem.tsx";
import { ThreadComposerFooter } from "./ThreadComposerFooter.tsx";
import { useOmniReview } from "./useOmniReview.ts";
import { StatusText } from "./StatusText.tsx";

/**
 * The open biscuits, stacked inside the response that explains them (between its
 * prose and its footer). They keep their own `messageId` — the biscuit moved,
 * the artifact still belongs to the message that produced it, and the artifact
 * query is keyed on both.
 */
const AnchoredBiscuits = ({
  threadId,
  biscuits,
}: {
  threadId: number;
  biscuits: readonly AnchoredBiscuit[];
}) => (
  <div className="flex flex-col gap-2">
    {biscuits.map(({ messageId, artifact }) => (
      <HeavyArtifactItem
        key={artifact.id}
        threadId={threadId}
        messageId={messageId}
        msgListArtifact={artifact}
      />
    ))}
  </div>
);

/**
 * Turn-root items don't have a single event ID. For event-backed roots, keep the
 * key consistent for events sent in session (clientChatId) so optimistic items
 * don't remount when the real event replaces them. Remounting triggers a Safari
 * bug where scroll position resets by the item's height — due to Safari's
 * handling of SVGs inside scroll views inside @containers.
 */
const rootItemKey = (item: RootItem, index: number): string | number => {
  if (item.kind === "turn") return item.node.turnId;
  if (item.kind === "cluster") {
    return `cluster-${item.items[0] ? childClusterItemKey(item.items[0]) : index}`;
  }
  return item.event.clientOnly?.clientChatId ?? item.event.id;
};

type StepItem = { key: string | number; item: RootItem };

/**
 * Identifies a folded run across the handoff from the thinking indicator to the
 * answer's eyebrow — the two disclosures are one control at two points in time,
 * so they have to agree on whether the user opened it.
 *
 * The run's first item is the same root item on both sides of the handoff, and
 * later steps only ever append, so its key holds still while the run grows.
 */
const runKey = (items: readonly StepItem[]): string | number | undefined => items[0]?.key;

/** The run itself, unrolled in feed order, each item rendered exactly as it
 *  would have been inline — a breadcrumb, or in the event view a whole turn
 *  group. Message footers are suppressed so the run reads as one block rather
 *  than a stack of individually actionable messages. */
const StepList = ({
  items,
  onDelete,
  sendMessage,
}: {
  items: readonly StepItem[];
  onDelete: (messageId: number) => void;
  sendMessage: SendMessageFn;
}) => (
  <CollapsibleContent className="flex flex-col">
    {items.map(({ key, item }) =>
      item.kind === "message" ? (
        <MessageItem
          key={key}
          message={item.event.message}
          sortDate={item.event.sortDate}
          clientOnly={item.event.clientOnly}
          onDelete={onDelete}
          sendMessage={sendMessage}
          isLastMessage={false}
          hideFooter
        />
      ) : (
        <div key={key} className={cn(breakWordsContainer)}>
          <RootItemRenderer item={item} />
        </div>
      ),
    )}
  </CollapsibleContent>
);

/**
 * The steps a turn took, sitting as an eyebrow above its answer. Collapsed it is
 * one muted line; expanding unrolls the run.
 */
const TurnSteps = ({
  items,
  onDelete,
  sendMessage,
  isOpen,
  onOpenChange,
}: {
  items: readonly StepItem[];
  onDelete: (messageId: number) => void;
  sendMessage: SendMessageFn;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <Collapsible open={isOpen} onOpenChange={onOpenChange} className="flex flex-col gap-2">
    {/* Same type and colour as a breadcrumb, minus its leading corner arrow:
        this labels the run rather than being one of them. The chevron rotates
        off `data-state` through the shared `CollapsibleTrigger` CSS. */}
    <CollapsibleTrigger
      className="CollapsibleTrigger w-fit text-xs text-left text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
      data-statsig-name="thread-turn-steps"
    >
      {stepsLabel(items.length)}
      <span className="whitespace-nowrap">
        &nbsp;
        <ChevronDown className="size-3.5 shrink-0 transition-transform duration-200 CollapsibleTriggerIcon inline-block" />
      </span>
    </CollapsibleTrigger>
    <StepList items={items} onDelete={onDelete} sendMessage={sendMessage} />
  </Collapsible>
);

/**
 * The same disclosure while the turn is still working, with the thinking
 * indicator as its own trigger.
 *
 * The eyebrow and this are one control at two points in time: the run folds
 * away the moment it starts, and something has to stand in for it until the
 * answer lands. The indicator is already there saying what okthx is doing, so it
 * labels the run rather than competing with a second line that would have to
 * invent a count for work still in flight.
 *
 * With nothing collected yet it stays a plain indicator — a toggle that opens on
 * to nothing is worse than no toggle.
 *
 * Its open state is owned by the feed rather than by this component, because the
 * component itself goes away at the handoff: the moment the answer lands the run
 * moves to that answer's eyebrow, and an uncontrolled disclosure would slam shut
 * at exactly the point the user finally had something to read (BIG-7475).
 *
 * Exported for its test only; the feed is its one caller.
 */
export const RunningSteps = ({
  thread,
  items,
  onDelete,
  sendMessage,
  isOpen,
  onOpenChange,
}: {
  thread: GetThreadResp;
  items: readonly StepItem[];
  onDelete: (messageId: number) => void;
  sendMessage: SendMessageFn;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  if (items.length === 0) return <StatusText thread={thread} className="self-start" />;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={onOpenChange}
      className="flex flex-col gap-2 self-start"
    >
      {/* The whole line is the trigger, not just the chevron: the words are the
          bigger target, and the eyebrow this hands off to opens on its label
          too (BIG-8268). */}
      <StatusText
        thread={thread}
        renderLine={(line) => (
          <CollapsibleTrigger
            className="CollapsibleTrigger w-fit flex items-center gap-1 text-left cursor-pointer hover:text-foreground transition-colors"
            data-statsig-name="thread-running-steps"
            aria-label="Show steps in progress"
          >
            {line}
            <ChevronDown className="size-3.5 shrink-0 transition-transform duration-200 CollapsibleTriggerIcon" />
          </CollapsibleTrigger>
        )}
      />
      <StepList items={items} onDelete={onDelete} sendMessage={sendMessage} />
    </Collapsible>
  );
};

export const MessageList = ({
  thread,
  contentWrapperClassName,
  onEscapeKeyPressed,
  focusType = "always",
  debugData,
  readOnly = false,
  isEventView = false,
  hideEmptyPlaceholder = false,
  scrollOverflowMode = "scroll",
}: {
  thread: GetThreadResp;
  contentWrapperClassName?: string;
  onEscapeKeyPressed?: () => boolean;
  focusType?: FocusType;
  debugData?: DebugDataByView;
  readOnly?: boolean;
  isEventView?: boolean;
  /**
   * Forwarded to the scroll wrapper. `"auto"` (used by the narrow calendar
   * thread panel) only shows a scrollbar when content overflows instead of
   * always reserving the gutter. See `ScreenOuterScrollWrapper`.
   */
  scrollOverflowMode?: "scroll" | "auto";
  /**
   * Suppress the empty-thread suggestions chip placeholder. Used by surfaces
   * that open a thread immediately after submitting its first message — there's
   * a brief window between `isPending` settling and the new message landing in
   * the cache where the placeholder would otherwise flash.
   */
  hideEmptyPlaceholder?: boolean;
}) => {
  const showExternalThreads = useGateValue("visible_external_threads");
  const unarchiveMutation = useUnarchiveThread({ threadId: thread.id });
  const handleUnarchive = useCallback(() => {
    unarchiveMutation.mutateAsync(thread.id);
  }, [unarchiveMutation, thread.id]);
  const {
    items,
    queuedUserItems,
    removeSessionEvent,
    addOptimisticUserMessage,
    isFetchingPreviousPage,
    isPending: isMessagesPending,
    isSending: isSendingMessage,
    error: messagesError,
    sendMessage,
    sendError,
    hasPreviousPage,
    loadMore,
    deleteMessages,
  } = useMessageList({
    threadId: thread.id,
    debugData,
    isEventView,
  });

  // Check if we have any active messages for spacer logic. Computed before the
  // scroll hook, whose autoscroll keys on this message's id.
  const activeUserMessage = useMemo(() => {
    const item = items.findLast((item) => item.clientOnly?.clientChatId !== undefined);
    return item?.type === "message" ? item.message : undefined;
  }, [items]);

  const {
    scrollContainerRef,
    activeUserMessageRef,
    bottomRef,
    isAtBottom,
    triggerLoadMoreRef,
    scrollContainerStyles,
    spacerHeight,
  } = useFeedScroll({
    threadId: thread.id,
    items,
    hasPreviousPage,
    isFetchingPreviousPage,
    loadMore,
    activeUserMessageId: activeUserMessage?.id,
  });

  const prefillOmniboxRef = useRef<((text: string) => void) | null>(null);
  const handleSetInputPrefill = useCallback((text: string) => {
    prefillOmniboxRef.current?.(text);
  }, []);

  const handleDeleteMessage = useCallback(
    (messageId: number) => {
      deleteMessages(messageId);
    },
    [deleteMessages],
  );

  const rootItems = useMemo(() => buildRootItems(items, isEventView), [items, isEventView]);

  // A card still waiting on the user reads as an instruction, so it belongs
  // under the answer that gives it — not up-thread at the moment okthx happened
  // to draft it (BIG-7272). The breadcrumb stays behind to mark that the work
  // happened. Gated off, nothing relocates or folds and the feed renders exactly
  // as it did before.
  const slimmerMessageView = useGateValue("slimmer_message_view");
  // Content is held back only while an answer is still coming. Once the thread
  // is idle nothing more will arrive to anchor it to, so it goes back to the
  // feed rather than staying hidden for good.
  const isTurnRunning =
    thread.activityState === "running" || thread.activityState === "stopPending";
  // The trace of how okthx got to an answer is worth keeping but not worth
  // reading every time, so the run of breadcrumbs between the request and the
  // answer folds into that answer's action bar, behind a toggle.
  // Operates on root items, not raw messages, so that the turn / cluster /
  // orphan groups fold away with the breadcrumbs they belong to instead of
  // being left stranded in the feed. Footer visibility and last-ness come from
  // the same pass, because all three are the same question: what does the feed
  // actually render? `isEventView` goes in for the same reason — whether the
  // reader wants the live trace is part of that question, not a second one.
  const {
    byResponseMessageId,
    runningIndices,
    foldedIndices,
    hideFooterByIndex,
    lastVisibleIndex,
    byAnchorMessageId,
    relocatedIds,
    reactionsByMessageId,
  } = useMemo(
    () =>
      selectFeedLayout(rootItems, {
        fold: slimmerMessageView,
        isTurnRunning,
        isEventView,
      }),
    [rootItems, slimmerMessageView, isEventView, isTurnRunning],
  );

  // Which folded runs the user has opened, keyed by `runKey` so a run keeps its
  // state when it moves from the thinking indicator to the answer's eyebrow.
  // Absent means closed, so a run that has never been touched starts folded.
  const [openRunKeys, setOpenRunKeys] = useState<ReadonlySet<string | number>>(
    () => new Set<string | number>(),
  );
  const setRunOpen = useCallback((key: string | number, open: boolean) => {
    setOpenRunKeys((prev) => {
      if (prev.has(key) === open) return prev;
      const next = new Set(prev);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  // Build both stacks here and hand them down as ready-made nodes, so the
  // answer can drop one between its prose and its footer, and one above itself,
  // without either learning how they render.
  const toStepItems = useCallback(
    (indices: readonly number[]): StepItem[] =>
      indices
        .map((index) => {
          const item = rootItems[index];
          return item ? { key: rootItemKey(item, index), item } : undefined;
        })
        .filter((entry): entry is StepItem => entry !== undefined),
    [rootItems],
  );

  const runningStepItems = useMemo(
    () => toStepItems(runningIndices),
    [toStepItems, runningIndices],
  );
  const runningRunKey = runKey(runningStepItems);

  // Memoized apart from the steps below: opening one run must not rebuild these,
  // or every anchored card in the thread re-renders on a click that had nothing
  // to do with it.
  const biscuitSlots = useMemo(() => {
    const slots = new Map<number, React.ReactNode>();
    for (const [anchorMessageId, biscuits] of byAnchorMessageId) {
      slots.set(anchorMessageId, <AnchoredBiscuits threadId={thread.id} biscuits={biscuits} />);
    }
    return slots;
  }, [byAnchorMessageId, thread.id]);

  const stepSlots = useMemo(() => {
    const slots = new Map<number, React.ReactNode>();
    for (const [responseMessageId, indices] of byResponseMessageId) {
      const folded = toStepItems(indices);
      const key = runKey(folded);
      if (key === undefined) continue;
      slots.set(
        responseMessageId,
        <TurnSteps
          items={folded}
          onDelete={handleDeleteMessage}
          sendMessage={sendMessage}
          isOpen={openRunKeys.has(key)}
          onOpenChange={(open) => setRunOpen(key, open)}
        />,
      );
    }
    return slots;
  }, [byResponseMessageId, toStepItems, handleDeleteMessage, sendMessage, openRunKeys, setRunOpen]);

  const relocatedSlots = useMemo(
    () => ({ biscuits: biscuitSlots, steps: stepSlots }),
    [biscuitSlots, stepSlots],
  );

  // The card review that takes over the composer when a turn leaves something
  // awaiting the user. It reads `items` and hands back what to render; its queue
  // state, staging and commit logic live in the hook.
  const review = useOmniReview({ thread, items, readOnly, addOptimisticUserMessage });
  const { isWebView } = useEmbedded();

  // use pending - only show the full loader on initial load
  if (isMessagesPending) return <Loader className="fixed inset-0" />;

  // only render an error if there are no items to show, ignore errors on additional pages
  if (messagesError && items.length === 0) return <ErrorMessage error={messagesError} />;

  return (
    <ScreenOuterScrollWrapper
      ref={scrollContainerRef}
      id="message-list-container"
      style={scrollContainerStyles}
      overflowMode={scrollOverflowMode}
    >
      <ScreenInnerScrollWrapper
        insetScrollbar={scrollOverflowMode === "scroll"}
        containerClassName={cn("pt-4", contentWrapperClassName)}
        // The Threads list insets its rows by `--panel-gutter`; the thread's
        // content must never sit closer to the pane edge than the list does,
        // so the default 1rem screen gutter is raised to match it.
        className="px-(--panel-gutter)"
      >
        {/* LoadMore trigger */}
        {hasPreviousPage && (
          <div ref={triggerLoadMoreRef}>
            <Loader size="sm" />
          </div>
        )}

        {items.length === 0 && !hideEmptyPlaceholder && (
          <div className="flex flex-1 items-center justify-center">
            <EmptyChatPlaceholder
              show={items.length === 0}
              setInputPrefill={handleSetInputPrefill}
            />
          </div>
        )}

        <RelocatedBiscuitProvider relocatedIds={relocatedIds} slots={relocatedSlots}>
          {rootItems.map((item, index) => {
            // Folded into a later answer's action bar — it renders there, so it
            // must not also render here.
            if (foldedIndices.has(index)) return null;

            const key = rootItemKey(item, index);

            // Messages are the only root item that isn't rendered through RootItemRenderer —
            // MessageItem has its own props (active-ref, footer, send/delete callbacks).
            if (item.kind === "message") {
              const message = item.event.message;
              const isActiveUserMessage = message.id === activeUserMessage?.id;
              const isLastMessage = index === lastVisibleIndex;
              const hideFooter = hideFooterByIndex.get(index) ?? false;

              return (
                <div
                  key={key}
                  ref={isActiveUserMessage ? activeUserMessageRef : undefined}
                  className={cn(breakWordsContainer)}
                >
                  <MessageItem
                    message={message}
                    sortDate={item.event.sortDate}
                    clientOnly={item.event.clientOnly}
                    onDelete={handleDeleteMessage}
                    sendMessage={sendMessage}
                    isLastMessage={isLastMessage}
                    hideFooter={hideFooter}
                    reactions={reactionsByMessageId.get(message.id)}
                    readOnly={readOnly}
                  />
                </div>
              );
            }

            return (
              <div key={key} className={cn(breakWordsContainer)}>
                <RootItemRenderer item={item} />
              </div>
            );
          })}
        </RelocatedBiscuitProvider>

        {/* Browser activity and actor steps have independent disclosure controls. */}
        <RunningSteps
          thread={thread}
          items={runningStepItems}
          onDelete={handleDeleteMessage}
          sendMessage={sendMessage}
          isOpen={runningRunKey !== undefined && openRunKeys.has(runningRunKey)}
          onOpenChange={(open) => {
            if (runningRunKey !== undefined) setRunOpen(runningRunKey, open);
          }}
        />

        {/* Virtual spacer to enable scrolling active messages to top */}
        {activeUserMessage && spacerHeight > 0 && (
          <div
            style={{ height: `${spacerHeight}px` }}
            // Pointer-events-none prevents the spacer from intercepting taps intended for the
            // sticky Omnibox that may visually overlap with it due to sticky positioning
            className="pointer-events-none"
            data-testid="virtual-spacer"
          />
        )}

        <ThreadComposerFooter
          thread={thread}
          review={review}
          readOnly={readOnly}
          isAtBottom={isAtBottom}
          scrollContainerRef={scrollContainerRef}
          queuedUserItems={queuedUserItems}
          removeSessionEvent={removeSessionEvent}
          showExternalThreads={showExternalThreads}
          isWebView={isWebView}
          onSendMessage={sendMessage}
          isSendingMessage={isSendingMessage}
          isMessagesPending={isMessagesPending}
          onEscapeKeyPressed={onEscapeKeyPressed}
          focusType={focusType}
          prefillOmniboxRef={prefillOmniboxRef}
          sendError={sendError}
          onUnarchive={handleUnarchive}
        />

        {/*
         * Sentinel to determine if we've reached the bottom of the list.
         */}
        <div ref={bottomRef} className={cn("visibility-hidden")} />
      </ScreenInnerScrollWrapper>
    </ScreenOuterScrollWrapper>
  );
};
